import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { ArrowLeft, ChevronDown, ChevronRight, Clock, LayoutGrid, Pin, PinOff, Star, X } from './icons';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { NavRow, NavSections } from './navParts';
import type {
  WsShellApp,
  WsShellAppGroup,
  WsShellModule,
  WsShellRecent,
  WsShellSection,
} from './shellTypes';
import { WS_COLOR, WS_FRAME, WS_STATUS } from './tokens';

export interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
  brand: string;
  appGroups: WsShellAppGroup[];
  /** The module the current screen belongs to; the drawer opens drilled into it. */
  activeModule?: WsShellModule;
  activeSectionKey?: string;
  activeTrailKeys?: readonly string[];
  onNavigate: (path: string) => void;
  /** Ids of starred applications. Omit `onToggleFavorite` to hide favourites altogether. */
  favoriteIds?: readonly string[];
  onToggleFavorite?: (id: string) => void;
  /** Screens visited lately, newest first. Omit or leave empty to hide the list. */
  recents?: readonly WsShellRecent[];
  versionLabel?: string;
  /**
   * Peek at an application's sections beside its row on hover (or ArrowRight). Needs a pointer
   * that can hover; on touch the chevron opens the sections inside the drawer instead.
   * Default on.
   */
  peek?: boolean;
  /** False only for a picture of the open drawer: no focus trap, no focus grab. */
  trapFocus?: boolean;
  /** Keep the drawer inside this element instead of the whole window (storyboard frames). */
  container?: HTMLElement | null;
  /**
   * DOCKED: the same drawer, pinned open as a column beside the screen instead of floating
   * over it — no scrim, no focus trap, and choosing a row does not close it. The shell lays the
   * column out; the drawer only draws itself in place.
   */
  docked?: boolean;
  /** Show the pin control. Omit to hide it (the drawer then cannot be pinned). */
  onTogglePin?: () => void;
}

const moduleOf = (app: WsShellApp): WsShellModule => ({
  id: app.id,
  label: app.label,
  icon: app.icon,
  path: app.homePath ?? app.path,
  sections: app.sections ?? [],
});

/**
 * Where every application lives when the screen needs the room (docs/ui-kit-app-shell-plan.md).
 *
 * A modal drawer over a scrim, closed until asked for. It keeps the drill-in model of
 * docs/architecture/navigation-and-breadcrumbs.md: the root lists the applications the person
 * can open; a chevron opens an application's sections inside the drawer without leaving the
 * screen, and choosing a row goes there and closes the drawer. It opens drilled into the
 * current application, so the section you are in is one press away.
 *
 * Focus is trapped while it is open, Escape and the scrim close it, and focus returns to the
 * button that opened it. It never persists anything itself: favourites and recents are props.
 */
export function NavDrawer({
  open,
  onClose,
  brand,
  appGroups,
  activeModule,
  activeSectionKey,
  activeTrailKeys,
  onNavigate,
  favoriteIds = [],
  onToggleFavorite,
  recents = [],
  versionLabel,
  peek = true,
  trapFocus = true,
  container,
  docked = false,
  onTogglePin,
}: NavDrawerProps) {
  /** The application whose sections are showing, or null for the list of applications. */
  const [viewId, setViewId] = useState<string | null>(activeModule?.id ?? null);
  const [recentsOpen, setRecentsOpen] = useState(false);
  useEffect(() => {
    if (open) {
      setViewId(activeModule?.id ?? null);
      setRecentsOpen(false);
    }
  }, [open, activeModule?.id]);

  /** The application whose sections are peeking beside its row, and where the row is. */
  const [peeking, setPeeking] = useState<{ id: string; top: number } | null>(null);
  const [hoverCapable, setHoverCapable] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const focusFlyout = useRef(false);

  useEffect(() => {
    const q = window.matchMedia?.('(hover: hover) and (pointer: fine)');
    setHoverCapable(Boolean(q?.matches));
  }, []);
  const canPeek = peek && hoverCapable;

  const clearTimers = useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  }, []);
  const closePeek = useCallback(() => {
    clearTimers();
    setPeeking(null);
  }, [clearTimers]);
  useEffect(() => {
    if (!open) closePeek();
    return clearTimers;
  }, [open, closePeek, clearTimers]);
  // Moving into a section list (or back to the list of applications) ends the peek.
  useEffect(() => closePeek(), [viewId, closePeek]);

  const peekAt = (app: WsShellApp, row: HTMLElement, delay: number) => {
    if (!canPeek || !app.sections?.length) return;
    clearTimers();
    openTimer.current = setTimeout(() => {
      const box = contentRef.current?.getBoundingClientRect();
      if (!box) return;
      rowRef.current = row.querySelector('button');
      setPeeking({ id: app.id, top: Math.max(0, row.getBoundingClientRect().top - box.top) });
    }, delay);
  };
  const leaveRow = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPeeking(null), 220);
  };

  // After ArrowRight opens it, move focus to the first item.
  useEffect(() => {
    if (peeking && focusFlyout.current) {
      focusFlyout.current = false;
      flyoutRef.current?.querySelector('button')?.focus();
    }
  }, [peeking]);

  const apps = appGroups.flatMap((g) => g.apps);
  const view: WsShellModule | undefined =
    viewId === null
      ? undefined
      : activeModule?.id === viewId
        ? activeModule
        : (() => {
            const app = apps.find((a) => a.id === viewId);
            return app ? moduleOf(app) : undefined;
          })();

  const go = (path: string) => {
    onNavigate(path);
    if (!docked) onClose();
  };

  const favorites = favoriteIds
    .map((id) => apps.find((a) => a.id === id))
    .filter((a): a is WsShellApp => Boolean(a));

  const appRow = (app: WsShellApp) => {
    const starred = favoriteIds.includes(app.id);
    return (
      <Box
        key={app.id}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        onMouseEnter={(e) => peekAt(app, e.currentTarget, 150)}
        onMouseLeave={leaveRow}
        onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
          if (e.key !== 'ArrowRight' || !app.sections?.length) return;
          if (!(e.target as HTMLElement).closest('[data-nav-main]')) return;
          e.preventDefault();
          focusFlyout.current = true;
          peekAt(app, e.currentTarget, 0);
        }}
      >
        <Box data-nav-main sx={{ flex: 1, minWidth: 0 }}>
          <NavRow
            label={app.label}
            icon={app.icon}
            current={app.id === activeModule?.id}
            onClick={() => go(app.path)}
          />
        </Box>
        {onToggleFavorite ? (
          <IconButton
            icon={Star}
            label={
              starred ? `Remove ${app.label} from favourites` : `Add ${app.label} to favourites`
            }
            aria-pressed={starred}
            size="small"
            onClick={() => onToggleFavorite(app.id)}
            sx={{ color: starred ? WS_STATUS.info.ink : WS_COLOR.textMuted }}
            iconFilled={starred}
          />
        ) : null}
        {app.sections?.length ? (
          <IconButton
            icon={ChevronRight}
            label={`Browse ${app.label}`}
            size="small"
            onClick={() => setViewId(app.id)}
          />
        ) : null}
      </Box>
    );
  };

  const heading = (text: string) => (
    <Typography
      variant="caption"
      component="h2"
      sx={{ display: 'block', px: 3, pt: 3, pb: 1, color: WS_COLOR.textMuted, fontWeight: 600 }}
    >
      {text}
    </Typography>
  );

  const bottomAt = view ? view.sections.findIndex((s: WsShellSection) => s.startsBottomGroup) : -1;
  const topSections =
    view && bottomAt >= 0 ? view.sections.slice(0, bottomAt) : (view?.sections ?? []);
  const bottomSections = view && bottomAt >= 0 ? view.sections.slice(bottomAt) : [];

  const body = (
      <Box
        ref={contentRef}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            height: WS_FRAME.barHeight,
            flex: 'none',
            borderBottom: `1px solid ${WS_COLOR.border}`,
          }}
        >
          {docked ? null : <IconButton icon={X} label="Close navigation" onClick={onClose} />}
          <Typography sx={{ fontWeight: 600, flex: 1, pl: docked ? 2 : 0 }}>{brand}</Typography>
          {onTogglePin ? (
            <IconButton
              icon={docked ? PinOff : Pin}
              label={docked ? 'Unpin navigation' : 'Pin navigation open'}
              title={docked ? 'Unpin — the menu closes and opens from ☰ again' : 'Pin — keep the menu open beside the screen'}
              aria-pressed={docked}
              onClick={onTogglePin}
            />
          ) : null}
        </Box>

        <Box
          component="nav"
          aria-label="Primary"
          sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <Box
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3 }}
            tabIndex={0}
            onScroll={closePeek}
          >
            {view ? (
              <>
                <NavRow label="Applications" icon={ArrowLeft} onClick={() => setViewId(null)} />
                <Box sx={{ my: 1, borderBottom: `1px solid ${WS_COLOR.border}` }} />
                <NavRow label={view.label} icon={view.icon} onClick={() => go(view.path)} />
                <NavSections
                  sections={topSections}
                  depth={1}
                  onGo={go}
                  {...(activeSectionKey ? { activeSectionKey } : {})}
                  {...(activeTrailKeys ? { activeTrailKeys } : {})}
                />
              </>
            ) : (
              <>
                {recents.length ? (
                  <Box sx={{ mb: 1 }}>
                    <NavRow
                      label="Recently visited"
                      icon={Clock}
                      expanded={recentsOpen}
                      onClick={() => setRecentsOpen((o) => !o)}
                      trailing={<Icon icon={recentsOpen ? ChevronDown : ChevronRight} size={16} />}
                    />
                    {recentsOpen ? (
                      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                        {recents.map((r) => (
                          <li key={r.path}>
                            <NavRow
                              label={r.context ? `${r.label} · ${r.context}` : r.label}
                              depth={1}
                              onClick={() => go(r.path)}
                            />
                          </li>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                ) : null}

                {onToggleFavorite ? (
                  <Box sx={{ mb: 1 }}>
                    {heading('Favourites')}
                    {favorites.length ? (
                      favorites.map(appRow)
                    ) : (
                      <Typography variant="body2" sx={{ px: 3, pb: 1, color: WS_COLOR.textMuted }}>
                        Starred applications appear here.
                      </Typography>
                    )}
                  </Box>
                ) : null}

                {appGroups.map((g) => (
                  <Box key={g.label} sx={{ mb: 1 }}>
                    {heading(g.label)}
                    {g.apps.map(appRow)}
                  </Box>
                ))}
              </>
            )}
          </Box>

          {bottomSections.length ? (
            <Box sx={{ flex: 'none', p: 3, borderTop: `1px solid ${WS_COLOR.border}` }}>
              <NavSections
                sections={bottomSections}
                depth={1}
                onGo={go}
                {...(activeSectionKey ? { activeSectionKey } : {})}
                {...(activeTrailKeys ? { activeTrailKeys } : {})}
              />
            </Box>
          ) : null}
        </Box>

        <Box sx={{ flex: 'none', p: 3, borderTop: `1px solid ${WS_COLOR.border}` }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Button
              variant="tonal"
              startIcon={<Icon icon={LayoutGrid} size={16} />}
              onClick={() => go('/')}
            >
              View all applications
            </Button>
            {versionLabel ? (
              <Typography variant="caption" sx={{ color: WS_COLOR.textMuted }}>
                {versionLabel}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {peeking
          ? (() => {
              const app = apps.find((a) => a.id === peeking.id);
              if (!app?.sections?.length) return null;
              const maxHeight = (contentRef.current?.clientHeight ?? 600) - peeking.top - 8;
              const back = () => {
                closePeek();
                rowRef.current?.focus();
              };
              return (
                <Box
                  ref={flyoutRef}
                  role="group"
                  aria-label={`${app.label} sections`}
                  onMouseEnter={() => clearTimeout(closeTimer.current)}
                  onMouseLeave={leaveRow}
                  onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
                    if (e.key === 'Escape' || e.key === 'ArrowLeft') {
                      e.preventDefault();
                      e.stopPropagation(); // closes the peek, not the whole drawer
                      back();
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    left: '100%',
                    top: peeking.top,
                    ml: 0.5,
                    minWidth: 240,
                    maxWidth: 320,
                    maxHeight: Math.max(160, maxHeight),
                    overflowY: 'auto',
                    p: 1,
                    backgroundColor: WS_COLOR.surface,
                    border: `1px solid ${WS_COLOR.border}`,
                    borderRadius: '12px',
                    boxShadow: WS_COLOR.menuShadow,
                  }}
                >
                  <PeekSections sections={app.sections} onGo={go} />
                </Box>
              );
            })()
          : null}
      </Box>
  );

  if (docked) {
    return (
      <Box
        role="complementary"
        aria-label="Navigation menu"
        sx={{
          gridColumn: 1,
          gridRow: 2,
          width: WS_FRAME.drawerWidth,
          minHeight: 0,
          backgroundColor: WS_COLOR.surface,
          borderRight: `1px solid ${WS_COLOR.border}`,
          // The peek hangs off the right edge over the screen, so it must sit above it.
          position: 'relative',
          zIndex: 2,
          overflow: 'visible',
        }}
      >
        {body}
      </Box>
    );
  }

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{
        ...(container
          ? {
              container,
              disableScrollLock: true,
              sx: { position: 'absolute' },
              slotProps: { backdrop: { sx: { position: 'absolute' } } },
            }
          : {}),
        ...(trapFocus
          ? {}
          : { disableEnforceFocus: true, disableAutoFocus: true, disableRestoreFocus: true }),
      }}
      PaperProps={{
        role: 'dialog',
        'aria-label': 'Navigation menu',
        // An edge-anchored surface keeps square corners (CLAUDE.md 1.9.2).
        sx: {
          width: WS_FRAME.drawerWidth,
          maxWidth: '90%',
          borderRadius: 0,
          // The peek hangs off the drawer's right edge; the drawer's own list scrolls inside it.
          overflow: 'visible',
          ...(container ? { position: 'absolute' } : {}),
        },
      }}
    >
      {body}
    </Drawer>
  );
}

/** The sections of an application as a flat list: buckets are headings, screens are rows. */
function PeekSections({
  sections,
  onGo,
  depth = 0,
}: {
  sections: readonly WsShellSection[];
  onGo: (path: string) => void;
  depth?: number;
}) {
  return (
    <>
      {sections.map((s) =>
        s.sections?.length ? (
          <Box key={s.key}>
            <Typography
              variant="caption"
              component="div"
              sx={{
                px: 3,
                pt: 2,
                pb: 0.5,
                pl: 3 + depth * 3,
                color: WS_COLOR.textMuted,
                fontWeight: 600,
              }}
            >
              {s.label}
            </Typography>
            <PeekSections sections={s.sections} onGo={onGo} depth={depth + 1} />
          </Box>
        ) : (
          <NavRow key={s.key} label={s.label} depth={depth} onClick={() => onGo(s.path)} />
        ),
      )}
    </>
  );
}
